import React, { useState, useContext, useLayoutEffect, useEffect } from "react";
import { useSelector } from "react-redux";
import ApiNode from "../../../app/api/admin/node/ApiNode";
import Page from "../../modules/page";
import { ContextPage } from "../../modules/page/pageContext";
import { ContextContent } from "../../modules/content/contentContext";
import InputWrapper from "../../components/input/__wrapper";
import InputRow from "../../components/input/__row";
import InputText from "../../components/inputs/text";
import InputNumber from "../../components/inputs/number/number";
import InputPassword from "../../components/inputs/password";
import InputTextarea from "../../components/inputs/textarea";
import InputDatetime from "../../components/inputs/datetime/datetime";
import Select from "../../components/inputs/select";
import InputCheckbox from "../../components/inputs/checkbox";
import Label from "../../components/inputs/label";
import MessageError from "../../components/message/__errors";
import Button from "../../components/button";
import Loader from "../../components/loader";
import Tooltip from "../../components/tooltip";
import ModalYesNo from "../../components/modals/modal-yes-no";
import Modal from "../../components/modals/modal";
import DateLib from "../../../inc/DateLib";
import Table from "../../modules/table";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import "./styles.css";

// Карточка ноды: show / edit / create + блок оркестрации (проба/провижининг/деплой).
export default function NodeShow({ ...other }) {
    return <Page ApiModel={ApiNode} {...other}>
        <NodeShowContent/>
    </Page>;
}

function NodeShowContent() {
    const contextContent = useContext(ContextContent);
    // {id: code} — из app-init; нужен, чтобы показать рекомендованный тариф именем, а не id.
    const deviceTypes = useSelector((state) => state.option.deviceTypes);
    // {id: label} — из app-init; все группы прокси для чекбоксов и подписей.
    const proxyGroups = useSelector((state) => state.option.proxyGroups);

    const [_id, set_id] = useState('');
    const [_name, set_name] = useState('');
    const [_wanIp, set_wanIp] = useState('');
    const [_sshPort, set_sshPort] = useState('22');
    const [_sshUser, set_sshUser] = useState('root');
    const [_sshSecret, set_sshSecret] = useState('');
    const [_maxDevices, set_maxDevices] = useState('');
    const [_arch, set_arch] = useState('');
    const [_osVersion, set_osVersion] = useState('');
    const [_kernel, set_kernel] = useState('');
    const [_facts, set_facts] = useState(null);
    const [_recommendedDeviceTypeId, set_recommendedDeviceTypeId] = useState(null);
    const [_bench, set_bench] = useState(null);
    const [_status, set_status] = useState('');
    const [_devicesCount, set_devicesCount] = useState('');
    const [_lanIp, set_lanIp] = useState('');
    const [_wgPubkey, set_wgPubkey] = useState('');
    const [_wgEndpoint, set_wgEndpoint] = useState('');
    const [_cpuTotal, set_cpuTotal] = useState('');
    const [_ramTotalMb, set_ramTotalMb] = useState('');
    const [_diskFreeGb, set_diskFreeGb] = useState('');
    const [_provisionLog, set_provisionLog] = useState('');
    const [_isResettingKnownHosts, set_isResettingKnownHosts] = useState(false);
    // Группы прокси ноды — отсортированный массив id. null — сервер поле не прислал (связь не
    // загружена): тогда и не отправляем, иначе диф принял бы это за «отвязать все».
    const [_proxyGroupIds, set_proxyGroupIds] = useState(null);

    contextContent._dataToServer.current = {
        name: _name,
        wan_ip: _wanIp,
        ssh_port: _sshPort,
        ssh_auth_login: _sshUser,
        password: _sshSecret,
        max_devices: _maxDevices,
    };
    // Набор групп целиком (заменяет прежний, [] — отвязать все). Отсортированный массив чисел —
    // так же, как его отдаёт NodeFormatter: диф шлёт поле, только если набор реально поменялся.
    // На создании пустой набор не шлём — групп у новой ноды и так нет.
    if ((_proxyGroupIds !== null) && !(contextContent.isCreate && (_proxyGroupIds.length === 0))) {
        contextContent._dataToServer.current.proxy_group_ids = _proxyGroupIds;
    }
    // Backend-валидация требует эти поля в каждом update (NodeRequest)
    contextContent._dataRequiredToServer.current = {
        name: _name,
        wan_ip: _wanIp,
        ssh_port: _sshPort,
        ssh_auth_login: _sshUser,
    };

    useLayoutEffect(() => {
        contextContent._handleSetData.current = (data) => {
            set_id(data.id);
            set_name(data.name ?? '');
            set_wanIp(data.wan_ip ?? '');
            set_sshPort(data.ssh_port ?? '22');
            set_sshUser(data.ssh_auth_login ?? '');
            set_sshSecret(''); // секрет назад не приходит; пусто = оставить прежний
            set_maxDevices(data.max_devices ?? '');
            set_arch(data.architecture ?? '');
            set_osVersion(data.os_version ?? '');
            set_kernel(data.kernel ?? '');
            set_facts(data.facts ?? null);
            set_recommendedDeviceTypeId(data.recommended_device_type_id ?? null);
            set_bench(data.bench ?? null);
            set_status(data.status ?? '');
            set_devicesCount(data.devices_count ?? 0);
            set_lanIp(data.lan_ip ?? '');
            set_wgPubkey(data.wg_pubkey ?? '');
            set_wgEndpoint(data.wg_endpoint ?? '');
            set_cpuTotal(data.cpu_total ?? '');
            set_ramTotalMb(data.ram_total_mb ?? '');
            set_diskFreeGb(data.disk_total_mb ?? '');
            set_provisionLog(data.provision_log ?? '');
            const proxyGroupIds = Array.isArray(data.proxy_group_ids) ? data.proxy_group_ids : null;
            set_proxyGroupIds(proxyGroupIds);
            contextContent.setTitle(`Нода: ${data.name}`);
        };
        if (contextContent.isCreate) {
            contextContent.setTitle('Новая нода');
            set_proxyGroupIds([]);
        }
        contextContent.doLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const readOnly = !contextContent.isCreateOrEdit;
    const errors = contextContent.errors ?? {};

    return <>
        {!contextContent.isCreate ?
            <InputWrapper>
                <InputText className="input_wf" value={_id} readOnly={true}>id</InputText>
            </InputWrapper> : null}

        <InputRow>
            <InputWrapper>
                <InputText className="input_wf" value={_name} readOnly={readOnly} errors={errors.name}
                    onChange={(e) => set_name(e.target.value)}>Имя</InputText>
            </InputWrapper>
            <InputWrapper>
                <InputText className="input_wf" value={_wanIp} readOnly={readOnly} errors={errors.wan_ip}
                    placeholder="ip или hostname" onChange={(e) => set_wanIp(e.target.value)}>WAN IP</InputText>
            </InputWrapper>
        </InputRow>

        <InputRow>
            <InputWrapper>
                <InputNumber className="input_wf" value={_sshPort} readOnly={readOnly} errors={errors.ssh_port}
                    onChange={(e) => set_sshPort(e.target.value)}>SSH-порт</InputNumber>
            </InputWrapper>
            <InputWrapper>
                <InputText className="input_wf" value={_sshUser} readOnly={readOnly} errors={errors.ssh_auth_login}
                    autoComplete="off" onChange={(e) => set_sshUser(e.target.value)}>SSH-пользователь</InputText>
            </InputWrapper>
        </InputRow>

        <InputRow>
            <InputWrapper>
            </InputWrapper>
            {contextContent.isCreateOrEdit ?
                <InputWrapper>
                    <InputPassword className="input_wf" value={_sshSecret} errors={errors.password}
                        placeholder={contextContent.isCreate ? 'Пароль или приватный ключ' : 'Пусто — оставить прежний'}
                        autoComplete="new-password"
                        onChange={(e) => set_sshSecret(e.target.value)}>SSH-секрет</InputPassword>
                </InputWrapper> : null}
        </InputRow>

        {contextContent.isEdit ?
            <InputWrapper>
                <Tooltip text="Если нода была переустановлена/пересоздана хостером, её SSH host-key меняется — панель тогда падает на «неверные креды» ещё до проверки логина/пароля (сам SSH обрывает соединение). Кнопка стирает старую запись из known_hosts панели и даёт кроном подхватить ноду заново; креды на нодe при этом не трогает.">
                    <Button disabled={_isResettingKnownHosts} onClick={async () => {
                        if (_isResettingKnownHosts || _id === '') return;
                        set_isResettingKnownHosts(true);
                        try {
                            const api = ApiNode.ResetKnownHosts(_id);
                            const data = await api.getResponse();
                            if (data && data.result) {
                                api.addMessages(`known_hosts очищен, статус ноды: ${data.status ?? '—'}`, 'success');
                            } else if (data && data.error) {
                                api.addMessages(data.error, 'error');
                            }
                        } finally {
                            set_isResettingKnownHosts(false);
                        }
                    }}>Сбросить known_hosts</Button>
                </Tooltip>
            </InputWrapper> : null}

        <InputRow>
            <InputWrapper>
                <InputNumber className="input_wf" value={_maxDevices} readOnly={readOnly} errors={errors.max_devices}
                    onChange={(e) => set_maxDevices(e.target.value)}>Максимум устройств</InputNumber>
            </InputWrapper>
            {!contextContent.isCreate ?
                <InputWrapper>
                    <InputText className="input_wf" value={_arch} readOnly={true}>Архитектура</InputText>
                </InputWrapper> : null}
        </InputRow>

        {_proxyGroupIds !== null ?
            <NodeProxyGroups
                proxyGroups={proxyGroups}
                value={_proxyGroupIds}
                readOnly={readOnly}
                // Ошибки и всего набора, и отдельных id (proxy_group_ids.0 — «повтор»/«нет такой группы»).
                errors={Object.keys(errors).filter((key) => ((key === 'proxy_group_ids') || key.startsWith('proxy_group_ids.')))
                    .flatMap((key) => errors[key])}
                onChange={set_proxyGroupIds}
            /> : null}

        {!contextContent.isCreate ? <>
            <InputRow>
                <InputWrapper>
                    <InputText className="input_wf" value={_status} readOnly={true}>Статус</InputText>
                </InputWrapper>
                <InputWrapper>
                    <InputText className="input_wf" value={_osVersion || '—'} readOnly={true}>ОС</InputText>
                </InputWrapper>
            </InputRow>
            <InputRow>
                <InputWrapper>
                    <InputText className="input_wf" value={_devicesCount} readOnly={true}>Устройств</InputText>
                </InputWrapper>
                <InputWrapper>
                    <InputText className="input_wf" value={_lanIp} readOnly={true}>LAN IP</InputText>
                </InputWrapper>
            </InputRow>
            <InputRow>
                <InputWrapper>
                    <InputText className="input_wf" value={_wgEndpoint} readOnly={true}>WireGuard Endpoint</InputText>
                </InputWrapper>
                <InputWrapper>
                    <InputText className="input_wf" value={_wgPubkey} readOnly={true}>WireGuard Pubkey</InputText>
                </InputWrapper>
            </InputRow>
            <InputRow>
                <InputWrapper>
                    <InputText className="input_wf"
                        value={`${_cpuTotal || '—'} CPU / ${_ramTotalMb || '—'} МБ RAM / ${_diskFreeGb || '—'} МБ диск (всего)`}
                        readOnly={true}>Железо (по пробе)</InputText>
                </InputWrapper>
                <InputWrapper>
                    <InputText className="input_wf" value={_kernel || '—'} readOnly={true}>Ядро</InputText>
                </InputWrapper>
            </InputRow>
            <InputRow>
                <InputWrapper>
                    {/* Вердикт замера, разрешённый в строку каталога по device_types.code.
                        Пусто — либо замера не было, либо советуемого фермой профиля в каталоге
                        нет (различать по самому паспорту ниже). На «Максимум устройств» не
                        влияет: ёмкость задаёт человек, рекомендация — справка. */}
                    <InputText className="input_wf" readOnly={true}
                        value={_recommendedDeviceTypeId
                            ? (deviceTypes[_recommendedDeviceTypeId] ?? `#${_recommendedDeviceTypeId}`)
                            : '—'}>Рекомендуемый тариф</InputText>
                </InputWrapper>
                <InputWrapper>
                </InputWrapper>
            </InputRow>
            {_provisionLog ?
                <InputWrapper className="nodes__provision-log">
                    <InputTextarea className="input_wf" value={_provisionLog} readOnly={true}>Лог провижининга</InputTextarea>
                </InputWrapper> : null}
        </> : null}

        {contextContent.isShow ?
            <NodeActions nodeId={_id} maxDevices={_maxDevices} onDone={contextContent.doLoad}/> : null}
        {contextContent.isShow ? <NodeCheckList check={_facts?.check}/> : null}
        {contextContent.isShow ? <NodeBench bench={_bench}/> : null}
        {contextContent.isShow ? <NodeFacts facts={_facts}/> : null}
        {contextContent.isShow ? <NodeScriptExecutes nodeId={_id}/> : null}
    </>;
}

// Группы прокси ноды (многие ко многим). В show — подписи через запятую, в edit/create — чекбоксы
// по всем группам справочника. value — отсортированный массив id; onChange получает новый такой же.
// Группа, которой нет в справочнике (удалена/справочник устарел), всё равно показывается — как #id,
// чтобы её можно было снять, а не потерять молча при сохранении.
function NodeProxyGroups({ proxyGroups, value, readOnly, errors, onChange }) {
    const labelOf = (id) => (proxyGroups[id] ?? `#${id}`);

    if (readOnly) {
        return <InputWrapper>
            <InputText className="input_wf" readOnly={true}
                value={(value.length > 0) ? value.map(labelOf).join(', ') : '—'}>Группы прокси</InputText>
        </InputWrapper>;
    }

    const ids = Array.from(new Set([...Object.keys(proxyGroups).map(Number), ...value])).sort((a, b) => (a - b));
    const toggle = (id, isChecked) => {
        const next = isChecked ? [...value.filter((item) => item !== id), id] : value.filter((item) => item !== id);
        onChange(next.sort((a, b) => (a - b)));
    };

    return <InputWrapper>
        <Label>Группы прокси</Label>
        {(ids.length > 0) ?
            <div className="nodes__proxy-groups-list">
                {ids.map((id) => <InputCheckbox key={id} value={value.includes(id)}
                    onChange={(e) => toggle(id, e.target.checked)}>{labelOf(id)}</InputCheckbox>)}
            </div>
            : <div className="nodes__actions-hint">Групп прокси нет — заводятся в разделе «Группы прокси».</div>}
        {errors ? <MessageError errors={errors}/> : null}
    </InputWrapper>;
}

// Блок оркестрации ноды (только show-режим): проба, провижининг (с подтверждением),
// деплой партии устройств по тарифу из state.option.deviceTypes.
// maxDevices — то, что сейчас в поле карточки: пересборка приводит количество именно к нему,
// и в подтверждении надо показать число, а не отсылать человека обратно к форме.
function NodeActions({ nodeId, maxDevices, onDone }) {
    const contextPage = useContext(ContextPage);
    const deviceTypes = useSelector((state) => state.option.deviceTypes);

    const [isBusy, setIsBusy] = useState(false);
    const [isOpenModalProvision, setIsOpenModalProvision] = useState(false);
    const [isOpenModalSync, setIsOpenModalSync] = useState(false);
    const [_deviceTypeId, set_deviceTypeId] = useState('');
    const [_count, set_count] = useState('1');
    // Свой тариф у пересборки, отдельно от деплойного: действия соседние, но разные —
    // общий селектор означал бы, что выбор для одной кнопки молча меняет смысл другой.
    const [_syncDeviceTypeId, set_syncDeviceTypeId] = useState('');

    if (!contextPage.checkPermission('edit')) return null;

    const runAction = async (buildApi, successMessage) => {
        if (isBusy || nodeId === '') return;
        setIsBusy(true);
        try {
            const api = buildApi();
            const data = await api.getResponse();
            if (data && data.result) {
                api.addMessages(successMessage(data), 'success');
                if (onDone) onDone();
            } else if (data && data.error) {
                api.addMessages(data.error, 'error');
            }
        } finally {
            setIsBusy(false);
        }
    };

    const handleProbe = () => runAction(
        () => ApiNode.Probe(nodeId),
        () => 'Проба выполнена',
    );

    const handleProvision = () => runAction(
        () => ApiNode.Provision(nodeId),
        (data) => `Провижининг выполнен (статус: ${data.status ?? '—'})`,
    );

    const handleHealth = () => runAction(
        () => ApiNode.Health(nodeId),
        () => 'Аудит ноды запущен (~30 с) — итог в задаче «Аудит ноды» на странице «Задачи»',
    );


    // Пересборка не сводится к успех/провал: часть устройств может снестись, часть — упасть.
    // Поэтому свой обработчик, а не runAction: показываем и что сделано, и что нет.
    const handleSyncDevices = async () => {
        if (isBusy || nodeId === '') return;
        setIsBusy(true);
        try {
            const api = ApiNode.SyncDevices(nodeId, { device_type_id: _syncDeviceTypeId });
            const data = await api.getResponse();
            if (!data) return;

            const removed = (data.removed ?? []).length;
            const created = (data.created ?? []).length;
            const rebuilt = (data.rebuilt ?? []).length;
            const parts = [];
            if (removed) parts.push(`удалено: ${removed}`);
            if (created) parts.push(`добавлено: ${created}`);
            if (rebuilt) parts.push(`пересборка: ${rebuilt}`);
            api.addMessages(
                parts.length
                    ? `Пересборка — ${parts.join(', ')} (тариф: ${data.device_type ?? '—'})`
                    : `Пересборка: устройств на ноде нет, добавлять нечего (максимум: ${data.max_devices})`,
                data.result ? 'success' : 'warning',
            );
            (data.errors ?? []).forEach((error) => api.addMessages(error, 'error'));
            if (onDone) onDone();
        } finally {
            setIsBusy(false);
        }
    };

    const openSyncModal = () => {
        if (_syncDeviceTypeId === '') {
            new ApiNode().addMessages('Выберите тариф для пересборки', 'warning');
            return;
        }
        setIsOpenModalSync(true);
    };

    const handleDeploy = () => {
        if (_deviceTypeId === '') {
            new ApiNode().addMessages('Выберите тариф для деплоя', 'warning');
            return;
        }
        // Установка теперь асинхронная (крон panel:process-new-devices) — строки создаются
        // сразу со статусом "new", реальный run.py install идёт в фоне отдельным тиком.
        runAction(
            () => ApiNode.Deploy(nodeId, { device_type_id: _deviceTypeId, count: _count }),
            (data) => `Поставлено в очередь: ${(data.created ?? []).length}`,
        );
    };

    return <div className="nodes__actions">
        <div className="nodes__actions-title">Оркестрация</div>
        <div className="nodes__actions-row">
            <Tooltip text="Разово снимает по SSH факты о железе ноды (архитектура, CPU, RAM, диск, binder) и обновляет карточку. Ничего не меняет на ноде.">
                <Button disabled={isBusy} onClick={handleProbe}>Проба</Button>
            </Tooltip>
            <Tooltip text="Полная настройка ноды по SSH: обновление пакетов, поднятие WireGuard-туннеля, создание служебных пользователей (pal/zam), блокировка входа по паролю с публичного IP, снятие метрик. Ноды со статусом «Новая» настраиваются автоматически кроном раз в минуту — жать сюда нужно только для немедленного повтора/теста.">
                <Button disabled={isBusy} onClick={() => setIsOpenModalProvision(true)}>Провижининг</Button>
            </Tooltip>
            <Tooltip text="Берёт у фермы факт по всем устройствам ноды и сверяет с панелью. Здоровье — у устройств, которые панель считает работающими: где на деле не работает (контейнер лежит, Android не загружен, потерян adb), ставит «Исправление устройства». Прокси — у запущенных контейнеров: где туннеля нет или он не тот — ставит установку, где туннель без назначения — снятие. Сама на устройства не ходит: исправления исполняют кроны в течение минуты. Итог и предупреждения (контейнеры вне панели, «остановлено, а запущено») — в задаче «Аудит ноды».">
                <Button disabled={isBusy} onClick={handleHealth}>Аудит ноды</Button>
            </Tooltip>
        </div>
        <div className="nodes__actions-row nodes__actions-row_deploy">
            <div className="nodes__actions-field">
                <Select className="input_wf" options={deviceTypes} value={_deviceTypeId} isNull={true}
                    onChange={(e) => set_deviceTypeId(e.target.value)}>Тариф</Select>
            </div>
            <div className="nodes__actions-field nodes__actions-field_count">
                <InputNumber className="input_wf" value={_count} min={1} max={20}
                    onChange={(e) => set_count(e.target.value)}>Количество</InputNumber>
            </div>
            <Tooltip text="Устройства создаются сразу со статусом «Новое» — фактическая установка (run.py) идёт в фоне кроном раз в минуту.">
                <Button className="btn-success" disabled={isBusy} onClick={handleDeploy}>Развернуть устройства</Button>
            </Tooltip>
        </div>
        <div className="nodes__actions-row nodes__actions-row_deploy">
            <div className="nodes__actions-field">
                <Select className="input_wf" options={deviceTypes} value={_syncDeviceTypeId} isNull={true}
                    onChange={(e) => set_syncDeviceTypeId(e.target.value)}>Тариф для пересборки</Select>
            </div>
            <Tooltip text="Приводит ферму ноды к заявленному состоянию: количество — к полю «Максимум устройств» карточки (лишние сносит последними по ID, недостающие добирает), а КАЖДОЕ оставшееся устройство пересобирает с нуля по выбранному здесь тарифу — контейнер и данные удаляются, устройство ставится заново. Пересборка идёт даже когда количество и тариф уже совпадают. Порядок: сначала измените число и сохраните ноду, потом выберите тариф и жмите сюда.">
                <Button disabled={isBusy} onClick={openSyncModal}>Пересобрать устройства</Button>
            </Tooltip>
        </div>
        <ModalYesNo
            isOpenModal={isOpenModalSync}
            closeModal={() => setIsOpenModalSync(false)}
            title={`Пересобрать устройства по тарифу «${deviceTypes?.[_syncDeviceTypeId] ?? _syncDeviceTypeId}»? Количество будет приведено к полю «Максимум устройств» — ${maxDevices || '—'} шт.: лишние удаляются совсем, недостающие добавляются. КАЖДОЕ оставшееся устройство пересобирается с нуля: контейнер и все его данные (аккаунты, приложения) удаляются, устройство ставится заново.`}
            actionBtnYes={() => {
                setIsOpenModalSync(false);
                handleSyncDevices();
            }}
            actionBtnNo={() => setIsOpenModalSync(false)}
        />
        <ModalYesNo
            isOpenModal={isOpenModalProvision}
            closeModal={() => setIsOpenModalProvision(false)}
            title="Запустить провижининг ноды? Операция длительная."
            actionBtnYes={() => {
                setIsOpenModalProvision(false);
                handleProvision();
            }}
            actionBtnNo={() => setIsOpenModalProvision(false)}
        />
    </div>;
}

// Чек-лист состояния ноды — nodes.facts.check, пишет скрипт «Проверка состояния ноды»
// (node-check). Отвечает не на «что это за машина» (это факты пробы ниже), а на «всё ли ещё
// так, как настроил провижн»: локдаун SSH, пользователи, туннель, дыра под adb, docker, ферма.
// [ключ, подпись, как показывать]; блока нет вовсе, пока скрипт ни разу не прогоняли.
const CHECK_ROWS = [
    ['sshd_lockdown_applied', 'Локдаун SSH применён',        'ok'],
    ['sshd_lockdown_file',    'Файл локдауна на месте',      'ok'],
    ['users_missing',         'Служебные пользователи',      'missing'],
    ['wg0_up',                'Туннель wg0 поднят',          'ok'],
    ['wg_service_enabled',    'Автозапуск wg-quick@wg0',     'ok'],
    ['wg_handshake_at',       'Рукопожатие WG',              'unixtime'],
    ['wg_mtu',                'MTU wg0',                     'text'],
    ['adb_reachable',         'adb доступен с хаба',         'ok'],
    ['docker_active',         'Docker запущен',              'ok'],
    ['docker_version',        'Версия Docker',               'text'],
    ['compose_version',       'Версия Compose',              'text'],
    ['storage_driver',        'Storage driver',              'text'],
    ['farm_version',          'Версия фермы',                'text'],
    ['redroid_images',        'Образы ReDroid',              'list'],
    ['reboot_required',       'Требуется перезагрузка',      'warn'],
    ['reboot_pkgs',           'Пакеты, ждущие перезагрузки', 'list'],
    ['unattended_upgrades',   'Автообновления',              'service'],
    ['fail2ban',              'fail2ban',                    'service'],
];

function renderCheckValue(kind, value) {
    switch (kind) {
        // Норма — истина: пусто/false тревожно.
        case 'ok':
            return <span className={`badge ${value ? 'badge_online' : 'badge_offline'}`}>{value ? 'да' : 'нет'}</span>;
        // Обратный случай — тревожна как раз истина (нода ждёт перезагрузки).
        case 'warn':
            return <span className={`badge ${value ? 'badge_warn' : 'badge_online'}`}>{value ? 'да' : 'нет'}</span>;
        case 'missing': {
            const missing = value ?? [];
            return missing.length === 0
                ? <span className="badge badge_online">все на месте</span>
                : <span className="badge badge_offline">нет: {missing.join(', ')}</span>;
        }
        // systemctl is-active/is-enabled как есть; пусто — юнита на ноде нет вовсе.
        case 'service':
            return <span className={`badge ${(value === 'active' || value === 'enabled') ? 'badge_online' : 'badge_warn'}`}>
                {value || 'нет'}
            </span>;
        case 'list':
            return (value ?? []).length === 0 ? '—' : value.join(', ');
        // wg отдаёт unix-время последнего рукопожатия; 0/null — его не было ни разу.
        case 'unixtime':
            return value ? DateLib.formatterDateTime(value * 1000) : '—';
        default:
            return (value === null || value === undefined || value === '') ? '—' : String(value);
    }
}

function NodeCheckList({ check }) {
    if (!check) return null;

    const header = [
        <ListHeaderItem key="label" name="label" isSort={false}>Проверка</ListHeaderItem>,
        <ListHeaderItem key="value" name="value" isSort={false}>Результат</ListHeaderItem>,
    ];

    const rows = CHECK_ROWS.map(([key, label, kind]) => ({ id: key, label, kind, value: check[key] }));

    const renderRow = (row) => [
        <ListBodyItem key="label">{row.label}</ListBodyItem>,
        <ListBodyItem key="value">{renderCheckValue(row.kind, row.value)}</ListBodyItem>,
    ];

    return <div className="nodes__actions">
        <div className="nodes__actions-title">Состояние ноды</div>
        <Table header={header} rows={rows} renderRow={renderRow}/>
        <div className="nodes__actions-hint">
            Снято: {DateLib.formatterDateTime(check.checked_at) || '—'} — скрипт «Проверка состояния ноды»
            на странице «Скрипты».
        </div>
    </div>;
}

// Перф-паспорт ноды — nodes.bench, пишет скрипт «bench» (run.py bench) с карточки ноды.
// Отличие от фактов: факты — паспортные данные (ядра, память), замер — фактическая
// производительность, из-за которой две ноды с одинаковыми 4 vCPU ведут себя по-разному
// (частота, turbo, steal). Идёт ~10 секунд, ноду не меняет.
// [путь в JSON, подпись]; вложенность плоская по точке — паспорт приходит блоками.
const BENCH_ROWS = [
    ['capability.arch',                  'Архитектура'],
    ['capability.binder',                'Binder'],
    ['capability.binder_ready',          'Binder свободен'],
    ['capability.virt',                  'Виртуализация'],
    ['capability.kernel',                'Ядро'],
    ['cpu.model',                        'Модель CPU'],
    ['cpu.vcpus',                        'vCPU'],
    ['cpu.threads_per_core',             'Потоков на ядро'],
    ['cpu.freq_base_mhz',                'Частота базовая, МГц'],
    ['cpu.freq_cur_mhz',                 'Частота текущая, МГц'],
    ['cpu.turbo',                        'Turbo'],
    ['cpu.single_thread.sha256_mbps',    'sha256 в один поток, МБ/с'],
    ['cpu.single_thread.busyloop_30M_s', 'busy-loop 30M, с'],
    ['cpu.steal_pct',                    'Steal, %'],
    ['memory.total_mb',                  'RAM всего, МБ'],
    ['memory.available_mb',              'RAM доступно, МБ'],
    ['memory.swap_mb',                   'Swap, МБ'],
    ['disk.total_gb',                    'Диск всего, ГБ'],
    ['disk.free_gb',                     'Диск свободно, ГБ'],
    ['disk.write_direct_mbps',           'Запись на диск, МБ/с'],
    ['load.avg1',                        'Load avg1'],
    ['load.nproc',                       'nproc'],
    ['load.containers_running',          'Контейнеров запущено'],
    ['verdict.max_devices',              'Вердикт фермы: устройств'],
    ['verdict.profile',                  'Вердикт фермы: тариф'],
];

const getByPath = (source, path) =>
    path.split('.').reduce((accumulator, key) =>
        (accumulator === null || accumulator === undefined) ? undefined : accumulator[key], source);

function NodeBench({ bench }) {
    if (!bench) return null;

    const header = [
        <ListHeaderItem key="label" name="label" isSort={false}>Показатель</ListHeaderItem>,
        <ListHeaderItem key="value" name="value" isSort={false}>Значение</ListHeaderItem>,
    ];

    const rows = BENCH_ROWS.map(([path, label]) => ({ id: path, label, value: getByPath(bench, path) }));

    const renderRow = (row) => [
        <ListBodyItem key="label">{row.label}</ListBodyItem>,
        <ListBodyItem key="value">{formatFactValue(row.value)}</ListBodyItem>,
    ];

    return <div className="nodes__actions">
        <div className="nodes__actions-title">Перф-паспорт (замер)</div>
        <Table header={header} rows={rows} renderRow={renderRow}/>
        <div className="nodes__actions-hint">
            Снято: {DateLib.formatterDateTime(bench.probed_at) || '—'} — скрипт «bench» на странице
            «Скрипты». Вердикт фермы носит справочный характер: ёмкость ноды задаёт поле
            «Максимум устройств», замер её не переписывает.
        </div>
    </div>;
}

// Факты пробы — nodes.facts без ключа check, пишет скрипт «Съём фактов о железе» (metrics).
// Подписи знаем не для всех ключей намеренно: набор растёт вместе с пробой на бэке, и
// незнакомый ключ показываем как есть — иначе новый факт молча пропадал бы из карточки.
const FACT_LABELS = {
    arch_raw: 'Архитектура (uname -m)',
    arch_family: 'Семейство архитектуры',
    needs_translation_for_arm: 'Нужен native-bridge для ARM',
    os_id: 'ID дистрибутива',
    os_version_id: 'Версия дистрибутива',
    os_codename: 'Кодовое имя',
    package_manager: 'Пакетный менеджер',
    init: 'Init',
    virt: 'Виртуализация',
    cpu_model: 'Модель CPU',
    swap_mb: 'Swap, МБ',
    binder_ready: 'Binder готов',
    docker: 'Docker установлен',
    adb: 'adb установлен',
    sshd_version: 'Версия sshd',
    sshd_include_present: 'sshd читает sshd_config.d',
};

function formatFactValue(value) {
    if (typeof value === 'boolean') return value ? 'да' : 'нет';
    if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
}

function NodeFacts({ facts }) {
    // check живёт в том же JSON, но это чек-лист, а не факт железа — он выше своим блоком.
    const entries = Object.entries(facts ?? {}).filter(([key]) => key !== 'check');
    if (entries.length === 0) return null;

    const header = [
        <ListHeaderItem key="label" name="label" isSort={false}>Факт</ListHeaderItem>,
        <ListHeaderItem key="value" name="value" isSort={false}>Значение</ListHeaderItem>,
    ];

    const rows = entries.map(([key, value]) => ({ id: key, label: FACT_LABELS[key] ?? key, value }));

    const renderRow = (row) => [
        <ListBodyItem key="label">{row.label}</ListBodyItem>,
        <ListBodyItem key="value">{formatFactValue(row.value)}</ListBodyItem>,
    ];

    return <div className="nodes__actions">
        <div className="nodes__actions-title">Факты пробы</div>
        <Table header={header} rows={rows} renderRow={renderRow}/>
    </div>;
}

// Статус запуска скрипта -> {label, badge-класс} (App\Enums\NodeScriptExecuteStatus).
const EXECUTE_STATUS = {
    'pending': { label: 'Ожидание', badge: 'badge_warn' },
    'running': { label: 'Выполняется', badge: 'badge_accent' },
    'success': { label: 'Успешно', badge: 'badge_online' },
    'error':   { label: 'Ошибка', badge: 'badge_offline' },
};

// История запусков SSH-скриптов ноды (App\Models\NodeScriptExecute) — только чтение.
// Детали шагов — в логе сервера (storage/logs/node-scripts-*.log), тут только статусы.
function NodeScriptExecutes({ nodeId }) {
    const [_items, set_items] = useState(null);

    useEffect(() => {
        if (!nodeId) return;
        let isCancelled = false;
        (async () => {
            const api = ApiNode.Executes(nodeId);
            const data = await api.getResponse();
            if (!isCancelled) set_items(data ?? []);
        })();
        return () => { isCancelled = true; };
    }, [nodeId]);

    // Пока грузится — показываем индикатор, а не пустоту: раньше блок появлялся из ниоткуда,
    // и до его появления карточка выглядела так, будто скрипты на ноде вообще не запускались.
    if (_items === null) {
        return <div className="nodes__actions">
            <div className="nodes__actions-title">История скриптов</div>
            <Loader/>
        </div>;
    }
    if (_items.length === 0) return null;

    const header = [
        <ListHeaderItem key="script" name="script" isSort={false}>Скрипт</ListHeaderItem>,
        <ListHeaderItem key="status" name="status" isSort={false}>Статус</ListHeaderItem>,
        <ListHeaderItem key="created_at" name="created_at" isSort={false}>Запущен</ListHeaderItem>,
        <ListHeaderItem key="updated_at" name="updated_at" isSort={false}>Обновлён</ListHeaderItem>,
    ];

    const renderRow = (row) => {
        const statusInfo = EXECUTE_STATUS[row.status] ?? { label: row.status, badge: '' };
        return [
            <ListBodyItem key="script">{row.script_label || row.script_name || '—'}</ListBodyItem>,
            <ListBodyItem key="status">
                <span className={`badge ${statusInfo.badge}`}>{statusInfo.label}</span>
            </ListBodyItem>,
            <ListBodyItem key="created_at">{DateLib.formatterDateTime(row.created_at) || '—'}</ListBodyItem>,
            <ListBodyItem key="updated_at">{DateLib.formatterDateTime(row.updated_at) || '—'}</ListBodyItem>,
        ];
    };

    return <div className="nodes__actions">
        <div className="nodes__actions-title">История скриптов</div>
        <Table header={header} rows={_items} renderRow={renderRow}/>
    </div>;
}
